/**
 * Persona Builder — converts PersonaIdentity into a {BAND-IDENTITY} prompt block
 * for injection into the generation pipeline.
 */

import type { PersonaIdentity } from '@/types/persona.types';

/**
 * Build a {BAND-IDENTITY} prompt block from a PersonaIdentity.
 * Crossroads (BandBible) → structured block; Band Persona → wrapped markdown.
 */
export function buildBandIdentityBlock(identity: PersonaIdentity): string {
  if (identity.bandBible && identity.source === 'crossroads') {
    return buildCrossroadsBlock(identity);
  }
  if (identity.markdownIdentity && identity.source === 'band-persona-architect') {
    return buildBandPersonaBlock(identity);
  }
  return buildManualBlock(identity);
}

function buildCrossroadsBlock(identity: PersonaIdentity): string {
  const bible = identity.bandBible as Record<string, unknown>;
  const lines: string[] = ['{BAND-IDENTITY}'];

  lines.push(`[Band_Name: ${identity.name}]`);
  lines.push(`[Genre: ${identity.genre}]`);

  if (bible.sonicIdentity) {
    lines.push(`[Sonic_Identity IS: ${String(bible.sonicIdentity)}]`);
  }
  if (bible.vocalIdentity) {
    lines.push(`[Vocal_Identity: ${String(bible.vocalIdentity)}]`);
  }
  if (bible.productionMix) {
    lines.push(`[Production_Mix: ${String(bible.productionMix)}]`);
  }
  if (bible.lyricalThemes) {
    lines.push(`[Lyrical_Themes: ${String(bible.lyricalThemes)}]`);
  }
  if (bible.influences) {
    lines.push(`[Influences: ${String(bible.influences)}]`);
  }
  if (bible.sunoStyle || identity.styleTags) {
    lines.push(`[Suno_Style: ${String(bible.sunoStyle || identity.styleTags)}]`);
  }
  if (bible.sunoExclude || identity.excludeTags) {
    lines.push(`[Suno_Exclude: ${String(bible.sunoExclude || identity.excludeTags)}]`);
  }
  // Include any additional top-level bible fields
  const knownKeys = new Set(['sonicIdentity', 'vocalIdentity', 'productionMix', 'lyricalThemes', 'influences', 'sunoStyle', 'sunoExclude', 'name', 'genre']);
  for (const [key, value] of Object.entries(bible)) {
    if (!knownKeys.has(key) && value && typeof value === 'string') {
      const tag = key.replace(/([A-Z])/g, '_$1').replace(/^_/, '');
      lines.push(`[${tag}: ${value}]`);
    }
  }

  lines.push('{/BAND-IDENTITY}');
  return lines.join('\n');
}

function buildBandPersonaBlock(identity: PersonaIdentity): string {
  const lines: string[] = ['{BAND-IDENTITY}'];
  lines.push(`[Band_Name: ${identity.name}]`);
  lines.push(`[Genre: ${identity.genre}]`);
  lines.push('');
  lines.push(identity.markdownIdentity!);
  lines.push('');
  if (identity.styleTags) {
    lines.push(`[Suno_Style: ${identity.styleTags}]`);
  }
  if (identity.excludeTags) {
    lines.push(`[Suno_Exclude: ${identity.excludeTags}]`);
  }
  lines.push('{/BAND-IDENTITY}');
  return lines.join('\n');
}

function buildManualBlock(identity: PersonaIdentity): string {
  if (!identity.name && !identity.genre) return '';
  const lines: string[] = ['{BAND-IDENTITY}'];
  lines.push(`[Band_Name: ${identity.name}]`);
  if (identity.genre) lines.push(`[Genre: ${identity.genre}]`);
  if (identity.styleTags) lines.push(`[Suno_Style: ${identity.styleTags}]`);
  if (identity.excludeTags) lines.push(`[Suno_Exclude: ${identity.excludeTags}]`);
  lines.push('{/BAND-IDENTITY}');
  return lines.join('\n');
}

/** Extract style tags from a PersonaIdentity */
export function extractPersonaStyleTags(identity: PersonaIdentity): string {
  if (identity.styleTags) return identity.styleTags;
  if (identity.bandBible && typeof identity.bandBible === 'object') {
    const bible = identity.bandBible as Record<string, unknown>;
    if (bible.sunoStyle) return String(bible.sunoStyle);
  }
  return '';
}

/** Extract exclude tags from a PersonaIdentity */
export function extractPersonaExcludeTags(identity: PersonaIdentity): string {
  if (identity.excludeTags) return identity.excludeTags;
  if (identity.bandBible && typeof identity.bandBible === 'object') {
    const bible = identity.bandBible as Record<string, unknown>;
    if (bible.sunoExclude) return String(bible.sunoExclude);
  }
  return '';
}
